import { useMemo } from 'react';
import {
  lastLoggedSetFromEntry,
  toCanonicalKg,
  type CurrentMax,
  type EffortScale,
  type LogSet,
  parseLocaleNumber,
} from '@heraion/shared';

import type { DraftEntry, DraftSet } from './state/loggerDraftTypes';
import { lastSessionEntryFromLogSets } from './lastSessionFromLogSets';
import {
  getLastLoggedSetForSlug,
  useEntryHistory,
  useEntryHistoryApiFetch,
} from './useEntryHistory';
import { NO_FETCH } from './useExerciseHistoryView';
import { useExerciseSetsFallback } from './useExerciseSetsFallback';
import {
  buildWeightCandidateRows,
  type WeightCandidateRow,
} from './weightCandidateRows';

const indexLastLogged = (sets: readonly LogSet[] | null) => {
  const last = sets ? lastSessionEntryFromLogSets(sets) : null;
  return last ? lastLoggedSetFromEntry(last) : undefined;
};

// LG-59 — the screen's side of the history widget's first section: which
// weights the app can work out for the set the athlete is standing on.
//
// ⛔ A hook and not five lines in TimelineLoggerScreen because of the unit:
// `DraftSet.weight` is a DISPLAY string in the set's own unit, and every
// number the chain deals in is canonical kg. That conversion is exactly the
// bug `useReplaceExercise` records having nearly shipped (lb compared against
// kg, every ratio out by 2.2), so it happens once, here, named.
export function useWeightCandidates({
  entry,
  set,
  currentMaxes,
  effortScale,
  t,
}: {
  entry: DraftEntry | null;
  set: DraftSet | null;
  /** Null until the maxes map has loaded — the same guard every other
   *  consumer of it uses. No max simply drops that instrument. */
  currentMaxes: Map<string, CurrentMax> | null;
  effortScale: EffortScale;
  t: (k: string, opts?: Record<string, unknown>) => string;
}): WeightCandidateRow[] {
  const slug = entry?.exerciseId ?? '';
  const { ready } = useEntryHistory(slug);
  const apiFetch = useEntryHistoryApiFetch();
  // The window holds 50 sessions: an exercise resumed after a break is not in
  // it. Ask the index only when the window is blind, as the history view does;
  // `ready` matters because before the window lands, empty is not an answer.
  const windowLast = slug !== '' ? getLastLoggedSetForSlug(slug) : null;
  const fallback = useExerciseSetsFallback(
    slug,
    slug !== '' && ready && apiFetch !== null && windowLast === null,
    apiFetch ?? NO_FETCH,
  );
  const fallbackSets = fallback.sets;
  return useMemo(() => {
    if (!entry || !set) return [];
    // CN-38 — the twin of `useReplaceExercise`'s line, with the same cause and
    // the same silence: `Number('72,5')` is NaN, the guard reads it as "no
    // weight typed", and the panel proposes as if the row were empty.
    const typed = parseLocaleNumber(set.weight);
    const weightKg =
      set.weight.trim() !== '' && Number.isFinite(typed)
        ? toCanonicalKg(typed, set.unit)
        : null;
    const reps = Number(set.reps);
    const max = currentMaxes?.get(entry.exerciseId);
    return buildWeightCandidateRows({
      set: {
        weightKg,
        weightSource: set.weightSource,
        weightFellBack: set.weightFellBack,
        rirPrescribed: set.rirPrescribed,
        reps: Number.isFinite(reps) ? reps : undefined,
        pctOfMax:
          set.intensityPrescribed?.kind === 'percent-of-max'
            ? set.intensityPrescribed.pct
            : undefined,
      },
      // ⛔ Passed WHOLE, not rebuilt field by field. `CurrentMax` is
      // assignable to `MaxForProposal`, and the hand-rebuilt literal that
      // used to stand here is exactly where `setAt` would have died on its
      // way to the age gate — the panel would then have ordered the rows
      // one way while the cell was filled another.
      max,
      lastLogged:
        getLastLoggedSetForSlug(entry.exerciseId) ??
        indexLastLogged(fallbackSets) ??
        undefined,
      effortScale,
      t,
    });
    // `t` changes identity on every language switch, which is precisely when
    // these strings must be rebuilt.
  }, [entry, set, currentMaxes, effortScale, t, fallbackSets]);
}
